// API Client with automatic key injection and error handling

import type {
  Job,
  JobsQueryParams,
  JobsResponse,
  LogsResponse,
  AssignmentsResponse,
  MetricsResponse,
  JobTypeResponse,
  CreateJobPayload,
  WorkersResponse,
  WorkersEnrichedResponse,
  WorkersQueryParams,
  WorkerDrainResponse,
  WorkerDrainStatusResponse,
  WorkflowTypesResponse,
  WorkflowVariableTypesResponse,
  WorkflowTemplatesResponse,
  WorkflowTemplate,
  WorkflowVariablesResponse,
  WorkflowVariable,
  WorkflowTypeDefaultsResponse,
  WorkflowTypeDefault,
  WorkflowType,
  CreateWorkflowTypePayload,
  CreateWorkflowTemplatePayload,
  UpdateWorkflowTemplatePayload,
  CreateWorkflowVariablePayload,
  CreateWorkflowTypeDefaultPayload,
  TemplateValidationResponse,
  ResolutionTestRequest,
  ResolutionTestResponse,
  CreateJobFromTemplatePayload,
} from './types';
import { buildJobsQuery } from './utils';

interface ApiConfig {
  baseUrl: string;
  apiKey: string;
}

class JobsApiClient {
  private config: ApiConfig;

  constructor(config: ApiConfig) {
    this.config = config;
  }

  private async request<T>(
    endpoint: string,
    options?: RequestInit
  ): Promise<T> {
    const url = `${this.config.baseUrl}${endpoint}`;
    const headers = {
      'x-api-key': this.config.apiKey,
      'Content-Type': 'application/json',
      ...options?.headers,
    };

    const response = await fetch(url, { ...options, headers });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Invalid API key');
      }
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} - ${errorText}`);
    }

    // Handle 204 No Content (DELETE responses)
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  // Jobs
  async getJobs(params?: JobsQueryParams, timeRangeFilter?: string | null): Promise<JobsResponse> {
    const queryParams = buildJobsQuery(params, timeRangeFilter);
    const query = queryParams.toString();
    return this.request(`/v1/jobs${query ? `?${query}` : ''}`);
  }

  async getJob(id: string): Promise<Job> {
    return this.request(`/v1/jobs/${id}`);
  }

  async getJobLogs(id: string, limit = 10000): Promise<LogsResponse> {
    return this.request(
      `/v1/jobs/${id}/logs?limit=${limit}&sort_by=log_ts:asc&fields=id,log_ts,level,message,attempt_number,worker_id`,
    );
  }

  async getJobAssignments(id: string): Promise<AssignmentsResponse> {
    return this.request(`/v1/jobs/${id}/assignments?fields=worker_id,create_ts,heartbeat_ts,release_ts`);
  }

  async getJobCallbackLogs(id: string): Promise<Job> {
    return this.request(`/v1/jobs/${id}?embed=callback_logs&fields=id,callback_logs`);
  }

  async getJobProviderAttempts(id: string): Promise<Job> {
    return this.request(`/v1/jobs/${id}?embed=provider_attempts&fields=id,provider_attempts`);
  }

  async getAllAssignments(): Promise<AssignmentsResponse> {
    return this.request('/v1/jobs/any/assignments?limit=500&fields=worker_id,create_ts,heartbeat_ts,release_ts');
  }

  async getMetrics(timeRange?: string): Promise<MetricsResponse> {
    const params = timeRange ? `?time_range=${timeRange}` : '';
    return this.request(`/v1/jobs/metrics${params}`);
  }

  // Job Types
  async getJobTypes(): Promise<JobTypeResponse> {
    return this.request('/v1/job-types');
  }

  // Create Job
  async createJob(payload: CreateJobPayload): Promise<Job> {
    return this.request('/v1/jobs', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Cancel Job
  async cancelJob(jobId: string): Promise<Job> {
    return this.request(`/v1/jobs/${jobId}/cancellation`, {
      method: 'POST',
    });
  }

  // Retry Job
  async retryJob(jobId: string): Promise<Job> {
    return this.request(`/v1/jobs/${jobId}/retry`, {
      method: 'POST',
    });
  }

  // Workers
  private buildWorkersQuery(params?: WorkersQueryParams): string {
    if (!params) return '';

    const queryParams = new URLSearchParams();

    if (params.q) queryParams.append('q', params.q);
    if (params.status) queryParams.append('status', params.status);
    if (params.worker_type) queryParams.append('worker_type', params.worker_type);
    if (params.gpu_model) queryParams.append('gpu_model', params.gpu_model);
    if (params.limit != null) queryParams.append('limit', params.limit.toString());
    if (params.offset != null) queryParams.append('offset', params.offset.toString());
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);

    const query = queryParams.toString();
    return query ? `?${query}` : '';
  }

  async getWorkers(params?: WorkersQueryParams): Promise<WorkersResponse> {
    return this.request(`/v1/workers${this.buildWorkersQuery(params)}`);
  }

  async getWorkersEnriched(params?: WorkersQueryParams): Promise<WorkersEnrichedResponse> {
    return this.request(`/v1/workers/enriched${this.buildWorkersQuery(params)}`);
  }

  // Worker drain / cordon
  async drainWorker(workerId: string): Promise<WorkerDrainResponse> {
    return this.request(`/v1/workers/${encodeURIComponent(workerId)}/drain`, {
      method: 'POST',
    });
  }

  async undrainWorker(workerId: string): Promise<WorkerDrainResponse> {
    return this.request(`/v1/workers/${encodeURIComponent(workerId)}/undrain`, {
      method: 'POST',
    });
  }

  async getWorkerDrainStatus(workerId: string): Promise<WorkerDrainStatusResponse> {
    return this.request(`/v1/workers/${encodeURIComponent(workerId)}/drain-status`);
  }

  // Worker Jobs - get jobs assigned to a specific worker
  async getWorkerJobs(workerId: string, params?: JobsQueryParams, timeRangeFilter?: string | null): Promise<JobsResponse> {
    const queryParams = buildJobsQuery(params, timeRangeFilter);
    const query = queryParams.toString();
    return this.request(`/v1/workers/${encodeURIComponent(workerId)}/jobs${query ? `?${query}` : ''}`);
  }

  // Test connection
  async testConnection(): Promise<boolean> {
    try {
      await this.getJobs({ limit: 1 });
      return true;
    } catch {
      return false;
    }
  }

  // Workflow Types
  async getWorkflowTypes(): Promise<WorkflowTypesResponse> {
    // High limit to fetch all types — backend defaults to 50 which truncates the searchable dropdown
    return this.request('/v1/workflow-types?limit=500');
  }

  async createWorkflowType(payload: CreateWorkflowTypePayload): Promise<WorkflowType> {
    return this.request('/v1/workflow-types', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getWorkflowType(name: string): Promise<WorkflowType> {
    return this.request(`/v1/workflow-types/${encodeURIComponent(name)}`);
  }

  async updateWorkflowType(name: string, payload: { description?: string }): Promise<WorkflowType> {
    return this.request(`/v1/workflow-types/${encodeURIComponent(name)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async deleteWorkflowType(name: string): Promise<void> {
    return this.request(`/v1/workflow-types/${encodeURIComponent(name)}`, {
      method: 'DELETE',
    });
  }

  // Workflow Variable Types (read-only)
  async getWorkflowVariableTypes(): Promise<WorkflowVariableTypesResponse> {
    return this.request('/v1/workflow-variable-types');
  }

  // Workflow Templates
  async getWorkflowTemplates(params?: {
    type_name?: string;
    environment?: string;
    limit?: number;
    offset?: number;
  }): Promise<WorkflowTemplatesResponse> {
    const queryParams = new URLSearchParams();
    if (params?.type_name) queryParams.append('q', `type_name.eq:'${encodeURIComponent(params.type_name)}'`);
    if (params?.environment) {
      const existing = queryParams.get('q');
      const envFilter = `environment.eq:'${encodeURIComponent(params.environment)}'`;
      queryParams.set('q', existing ? `${existing} AND ${envFilter}` : envFilter);
    }
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    queryParams.append('embed', 'variables');
    const query = queryParams.toString();
    return this.request(`/v1/workflow-templates${query ? `?${query}` : ''}`);
  }

  async getWorkflowTemplate(id: string): Promise<WorkflowTemplate> {
    return this.request(`/v1/workflow-templates/${id}?embed=variables`);
  }

  async createWorkflowTemplate(payload: CreateWorkflowTemplatePayload): Promise<WorkflowTemplate> {
    return this.request('/v1/workflow-templates', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateWorkflowTemplate(id: string, payload: UpdateWorkflowTemplatePayload): Promise<WorkflowTemplate> {
    return this.request(`/v1/workflow-templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async deleteWorkflowTemplate(id: string): Promise<void> {
    return this.request(`/v1/workflow-templates/${id}`, {
      method: 'DELETE',
    });
  }

  async getTemplateValidation(id: string): Promise<TemplateValidationResponse> {
    return this.request(`/v1/workflow-templates/${id}/validation`);
  }

  // Workflow Variables (nested under templates)
  async getTemplateVariables(templateId: string): Promise<WorkflowVariablesResponse> {
    return this.request(`/v1/workflow-templates/${templateId}/variables`);
  }

  async createTemplateVariable(
    templateId: string,
    payload: CreateWorkflowVariablePayload
  ): Promise<WorkflowVariable> {
    return this.request(`/v1/workflow-templates/${templateId}/variables`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateTemplateVariable(
    templateId: string,
    variableId: string,
    payload: Partial<CreateWorkflowVariablePayload>
  ): Promise<WorkflowVariable> {
    return this.request(`/v1/workflow-templates/${templateId}/variables/${variableId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async deleteTemplateVariable(templateId: string, variableId: string): Promise<void> {
    return this.request(`/v1/workflow-templates/${templateId}/variables/${variableId}`, {
      method: 'DELETE',
    });
  }

  async copyTemplateVariables(destTemplateId: string, sourceTemplateId: string): Promise<{ items: WorkflowVariable[] }> {
    return this.request(`/v1/workflow-templates/${destTemplateId}/copy-variables`, {
      method: 'POST',
      body: JSON.stringify({ source_template_id: sourceTemplateId }),
    });
  }

  // Workflow Type Defaults
  async getWorkflowTypeDefaults(params?: {
    type_name?: string;
    environment?: string;
  }): Promise<WorkflowTypeDefaultsResponse> {
    const queryParams = new URLSearchParams();
    const filters: string[] = [];
    if (params?.type_name) filters.push(`type_name.eq:'${encodeURIComponent(params.type_name)}'`);
    if (params?.environment) filters.push(`environment.eq:'${encodeURIComponent(params.environment)}'`);
    if (filters.length) queryParams.append('q', filters.join(' AND '));
    const query = queryParams.toString();
    return this.request(`/v1/workflow-type-defaults${query ? `?${query}` : ''}`);
  }

  async createWorkflowTypeDefault(payload: CreateWorkflowTypeDefaultPayload): Promise<WorkflowTypeDefault> {
    return this.request('/v1/workflow-type-defaults', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateWorkflowTypeDefault(
    id: string,
    payload: Partial<CreateWorkflowTypeDefaultPayload>
  ): Promise<WorkflowTypeDefault> {
    return this.request(`/v1/workflow-type-defaults/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async deleteWorkflowTypeDefault(id: string): Promise<void> {
    return this.request(`/v1/workflow-type-defaults/${id}`, {
      method: 'DELETE',
    });
  }

  // Resolution Testing
  async testResolution(request: ResolutionTestRequest): Promise<ResolutionTestResponse> {
    return this.request('/v1/workflow-templates/resolution', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Create Job from Template
  async createJobFromTemplate(payload: CreateJobFromTemplatePayload): Promise<Job> {
    return this.request('/v1/jobs/from-template', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }
}

export default JobsApiClient;
